import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { 
  Wallet, Smartphone, CreditCard, CheckCircle2, ArrowRight, 
  Crown, Sparkles, Shield, Gift, Coins, Zap, Lock, AlertCircle,
  Loader2, ExternalLink, Copy, Timer, Receipt, Activity, Bitcoin,
  ChevronRight, Star, Flame, Check, ShieldCheck
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "../components/UserBadge";
import RechargeModal from "../components/wallet/RechargeModal";
import VipPurchaseModal from "../components/wallet/VipPurchaseModal";
import VoucherModal from "../components/wallet/VoucherModal";
import TransactionHistory from "../components/wallet/TransactionHistory";

const PACKAGES = [
  { 
    id: "starter", 
    name: "STARTER PACK", 
    coins: 500, 
    bonus_coins: 0, 
    price_eur: 4.99, 
    color: "#38bdf8", 
    badge: "STARTER",
    desc: "Perfect pentru un prim abonament VIP și acces la funcții premium."
  },
  { 
    id: "supporter", 
    name: "SUPPORTER PACK", 
    coins: 1000, 
    bonus_coins: 200, 
    price_eur: 9.99, 
    color: "#10b981", 
    badge: "+20% BONUS",
    desc: "Alegerea ideală pentru jucătorii activi de mix-uri și servere CS."
  },
  { 
    id: "pro", 
    name: "PRO GAMER PACK", 
    coins: 2000, 
    bonus_coins: 800, 
    price_eur: 19.99, 
    color: "#f97316", 
    popular: true, 
    badge: "CEL MAI POPULAR",
    desc: "Cel mai bun raport valoare/preț. Deblochează skin-uri și VIP Gold."
  },
  { 
    id: "elite", 
    name: "ELITE SQUAD PACK", 
    coins: 5000, 
    bonus_coins: 2500, 
    price_eur: 49.99, 
    color: "#eab308", 
    badge: "BEST VALUE (+50%)",
    desc: "Pachet premium pentru lideri de clanuri, organizatori de mix-uri și donatori."
  },
  { 
    id: "champion", 
    name: "CHAMPION WHALE PACK", 
    coins: 10000, 
    bonus_coins: 6000, 
    price_eur: 99.99, 
    color: "#ec4899", 
    badge: "VIP MAX (+60%)",
    desc: "Tezaurul suprem Indungi x PlayZek. Acces Donator Diamond Permanent."
  },
];

export default function Donate() {
  const { user, setUser } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "recharge";

  const [tab, setTab] = useState(initialTab);
  const [packages, setPackages] = useState(PACKAGES);
  const [balance, setBalance] = useState(null);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    if (user) {
      api.get("/wallet/balance").then(r => setBalance(r.data)).catch(() => {});
    }
    api.get("/wallet/packages").then(r => {
      if (r.data && r.data.length > 0) setPackages(r.data);
    }).catch(() => {});
  }, [user]);

  const idgCoins = balance?.idg_coins ?? user?.idg_coins ?? 0;
  const vipTier = balance?.vip_tier ?? user?.vip_tier ?? "none";
  const vipUntil = balance?.vip_until ?? user?.vip_until;

  const vipTiers = [
    {
      id: "bronze",
      name: "VIP BRONZE",
      coins: 500,
      period: "30 Zile",
      color: "#cd7f32",
      badge: "ENTRY TIER",
      features: [
        "Tag [VIP] exclusiv în chat pe serverele de joc",
        "Slot rezervat la conectare (Connect VIP)",
        "Stiluri de nume animate și colorate pe forum",
        "+500 Puncte lunare de activitate pe profil",
        "Acces la comenzi VIP de bază în joc"
      ]
    },
    {
      id: "gold",
      name: "VIP GOLD",
      coins: 1000,
      period: "90 Zile",
      color: "#f59e0b",
      popular: true,
      badge: "RECOMANDAT",
      features: [
        "Toate avantajele incluse în pachetul VIP Bronze",
        "Arme & Skin-uri exclusive în Loadout de CS",
        "Cadre VIP animate cu efect neon pentru avatar",
        "Multiplicator dublu (2x) de reputație și puncte",
        "Acces în camera privată Staff & VIP Discord"
      ]
    },
    {
      id: "diamond",
      name: "DONATOR DIAMOND",
      coins: 2500,
      period: "Permanent",
      color: "#00e5ff",
      badge: "PERMANENT / ELITE",
      features: [
        "Toate avantajele VIP Gold Permanent (fără expirare)",
        "Rol & Culoare unică pe Discord-ul Oficial",
        "Imunitate la kick/slap pe toate serverele comunității",
        "Acces la toate Case Openings gratuite",
        "Insignă Donator Fondator afișată pe profil"
      ]
    },
  ];

  const handleRechargeSuccess = (newBalance) => {
    api.get("/wallet/balance").then(r => setBalance(r.data)).catch(() => {});
    if (setUser && newBalance !== null && newBalance !== undefined) {
      setUser(u => ({ ...u, idg_coins: newBalance }));
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-12" data-testid="donate-page">
      
      {/* ─── 1. HERO VAULT & WALLET STATUS HEADER ─── */}
      <div className="relative rounded-2xl overflow-hidden border border-[#161f30] bg-[#070a10] shadow-2xl p-6 sm:p-8">
        {/* Subtle Ambient Radial Glows */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[radial-gradient(ellipse_at_top_right,rgba(245,158,11,0.08),transparent_70%)] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-[radial-gradient(ellipse_at_bottom_left,rgba(0,255,136,0.06),transparent_70%)] pointer-events-none" />

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          
          {/* Left info */}
          <div className="space-y-3 max-w-2xl">
            <div className="flex items-center gap-2.5 flex-wrap">
              <span className="px-2.5 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-[10px] font-mono font-black uppercase tracking-widest flex items-center gap-1.5">
                <Coins className="w-3.5 h-3.5" />
                <span>INDUNGI x PLAYZEK VAULT</span>
              </span>
              <span className="text-[10px] font-mono text-emerald-400 flex items-center gap-1 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Gateway Automat Activ 24/7
              </span>
            </div>

            <h1 className="text-2xl sm:text-4xl font-chakra font-black uppercase text-white tracking-tight leading-none">
              PORTOFEL, PLĂȚI & <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">SERVICII VIP</span>
            </h1>

            <p className="text-zinc-400 text-xs sm:text-sm font-sans leading-relaxed">
              Achiziționează IDG Coins instant cu Card Bancar, PayPal, Revolut, Crypto sau PaysafeCard. Toate serviciile și abonamentele VIP se activează automat pe servere în maxim 5 secunde.
            </p>
          </div>

          {/* Right: Player Wallet Badge */}
          {user ? (
            <div className="bg-[#05070d]/90 border border-[#1a2336] rounded-xl p-4 sm:p-5 flex items-center gap-4 relative shrink-0 shadow-xl">
              <div className="relative">
                <Avatar user={user} size={54} />
              </div>
              <div className="space-y-1">
                <div className="text-[10px] font-chakra font-black uppercase text-zinc-400 tracking-wider">
                  Soldul Tău Actual
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="font-chakra text-3xl font-black text-amber-400 drop-shadow-[0_0_12px_rgba(245,158,11,0.3)]">
                    {idgCoins.toLocaleString()}
                  </span>
                  <span className="text-xs font-mono text-zinc-400 font-bold">IDG Coins</span>
                </div>
                <div className="text-[10px] font-mono text-zinc-400">
                  Statut VIP: <strong className="text-amber-300 uppercase">{vipTier !== "none" ? vipTier : "Inactiv"}</strong>
                  {vipUntil && <span className="text-zinc-500"> (expiră: {vipUntil.slice(0, 10)})</span>}
                </div>
              </div>

              <div className="flex flex-col gap-1.5 pl-2 border-l border-[#161f30]">
                <button
                  onClick={() => setModal("recharge")}
                  className="px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-chakra font-black text-xs uppercase tracking-wider flex items-center gap-1.5 transition-all shadow-[0_0_15px_rgba(245,158,11,0.25)] hover:scale-105"
                >
                  <Coins className="w-3.5 h-3.5" />
                  <span>Reîncarcă</span>
                </button>
                <button
                  onClick={() => setModal("voucher")}
                  className="px-4 py-1.5 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-zinc-300 hover:text-white font-chakra font-bold text-[11px] uppercase tracking-wider flex items-center gap-1 transition-all"
                >
                  <Gift className="w-3 h-3 text-violet-400" />
                  <span>Voucher PIN</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-[#05070d]/90 border border-[#1a2336] rounded-xl p-5 text-center space-y-3 shrink-0 shadow-xl">
              <div className="text-xs font-chakra font-bold uppercase text-zinc-300">
                Conectează-te pentru a cumpăra
              </div>
              <Link
                to="/login"
                className="block px-6 py-2.5 rounded-lg bg-[var(--tf-primary)] hover:bg-[#ff6b2b] text-black font-chakra font-black text-xs uppercase tracking-wider transition-all shadow-md"
              >
                AUTENTIFICARE
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* ─── 2. TACTICAL NAVIGATION TABS ─── */}
      <div className="flex items-center gap-2 border-b border-[#141b2a] pb-1 overflow-x-auto">
        <button
          onClick={() => setTab("recharge")}
          className={`flex items-center gap-2 px-5 py-3 rounded-t-lg text-xs font-chakra font-black uppercase tracking-wider transition-all border-t-2 ${
            tab === "recharge"
              ? "bg-[#080c14] border-[var(--tf-primary)] text-white shadow-md"
              : "border-transparent text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.02]"
          }`}
        >
          <Coins className="w-4 h-4 text-amber-400" />
          <span>CUMPĂRĂ IDG COINS</span>
        </button>

        <button
          onClick={() => setTab("vip")}
          className={`flex items-center gap-2 px-5 py-3 rounded-t-lg text-xs font-chakra font-black uppercase tracking-wider transition-all border-t-2 ${
            tab === "vip"
              ? "bg-[#080c14] border-amber-400 text-white shadow-md"
              : "border-transparent text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.02]"
          }`}
        >
          <Crown className="w-4 h-4 text-amber-400" />
          <span>ABONAMENTE VIP</span>
        </button>

        <button
          onClick={() => setTab("voucher")}
          className={`flex items-center gap-2 px-5 py-3 rounded-t-lg text-xs font-chakra font-black uppercase tracking-wider transition-all border-t-2 ${
            tab === "voucher"
              ? "bg-[#080c14] border-violet-400 text-white shadow-md"
              : "border-transparent text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.02]"
          }`}
        >
          <Gift className="w-4 h-4 text-violet-400" />
          <span>REVENDICĂ VOUCHER PIN</span>
        </button>

        {user && (
          <button
            onClick={() => setTab("history")}
            className={`flex items-center gap-2 px-5 py-3 rounded-t-lg text-xs font-chakra font-black uppercase tracking-wider transition-all border-t-2 ${
              tab === "history"
                ? "bg-[#080c14] border-cyan-400 text-white shadow-md"
                : "border-transparent text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.02]"
            }`}
          >
            <Receipt className="w-4 h-4 text-cyan-400" />
            <span>ISTORIC TRANZACȚII</span>
          </button>
        )}
      </div>

      {/* ─── TAB 1: CUMPĂRĂ IDG COINS ─── */}
      {tab === "recharge" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-chakra font-black uppercase text-white tracking-wide flex items-center gap-2">
                <Coins className="w-5 h-5 text-amber-400" />
                <span>PACHETE DISPONIBILE IDG COINS</span>
              </h2>
              <p className="text-xs font-mono text-zinc-400 mt-0.5">
                Selectează un pachet pentru a deschide gateway-ul securizat de plată instantanee.
              </p>
            </div>
            <div className="hidden sm:flex items-center gap-2 text-xs font-mono text-emerald-400 bg-emerald-500/10 px-3 py-1.5 rounded-lg border border-emerald-500/20">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <span>Plăți Securizate 256-bit SSL</span>
            </div>
          </div>

          {/* 5-Column Tailored Vault Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {packages.map((pkg) => {
              const totalCoins = (pkg.coins || 0) + (pkg.bonus_coins || 0);
              const isPopular = pkg.popular;

              return (
                <div
                  key={pkg.id}
                  className={`bg-[#080c14] border rounded-xl p-5 flex flex-col justify-between relative transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl group ${
                    isPopular 
                      ? "border-amber-500/60 shadow-[0_0_25px_rgba(245,158,11,0.15)] bg-gradient-to-b from-amber-500/10 via-[#080c14] to-[#080c14]"
                      : "border-[#161f30] hover:border-white/20"
                  }`}
                >
                  {/* Top Badge */}
                  {pkg.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-[9px] font-chakra font-black uppercase tracking-wider shadow-md whitespace-nowrap"
                      style={{
                        backgroundColor: isPopular ? "#f59e0b" : "#121824",
                        color: isPopular ? "#000" : pkg.color || "#fff",
                        border: isPopular ? "none" : `1px solid ${pkg.color}60`
                      }}
                    >
                      {pkg.badge}
                    </div>
                  )}

                  <div>
                    {/* Header */}
                    <div className="pt-2 text-center">
                      <div className="text-xs font-chakra font-black uppercase text-zinc-300 tracking-wider">
                        {pkg.name}
                      </div>
                      
                      {/* Coins Big Number */}
                      <div className="flex items-baseline justify-center gap-1.5 my-3">
                        <span className="font-chakra text-4xl font-black" style={{ color: pkg.color || "#fff" }}>
                          {totalCoins.toLocaleString()}
                        </span>
                        <span className="text-xs font-mono text-zinc-400 font-bold">IDG</span>
                      </div>

                      {/* Coins Math */}
                      {pkg.bonus_coins > 0 ? (
                        <div className="text-[10px] font-mono text-zinc-400 bg-black/40 py-1 px-2 rounded-md border border-white/5 inline-block">
                          {pkg.coins?.toLocaleString()} + <strong style={{ color: pkg.color }}>+{pkg.bonus_coins?.toLocaleString()} bonus</strong>
                        </div>
                      ) : (
                        <div className="text-[10px] font-mono text-zinc-400 py-1">
                          Pachet de bază
                        </div>
                      )}
                    </div>

                    <p className="text-[11px] text-zinc-400 text-center font-sans mt-3 leading-relaxed">
                      {pkg.desc}
                    </p>
                  </div>

                  {/* Price & Action Button */}
                  <div className="mt-6 pt-4 border-t border-[#141b2a] space-y-3">
                    <div className="text-center font-chakra font-black text-2xl text-white">
                      €{pkg.price_eur ?? pkg.price}
                    </div>

                    <button
                      onClick={() => setModal("recharge")}
                      className="w-full py-2.5 rounded-lg font-chakra font-black text-xs uppercase tracking-wider text-black transition-all shadow-md hover:brightness-110 flex items-center justify-center gap-1.5"
                      style={{ backgroundColor: pkg.color || "var(--tf-primary)" }}
                    >
                      <span>CUMPĂRĂ</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Payment Methods Visual Showcase */}
          <div className="bg-[#080c14] border border-[#161f30] rounded-xl p-6 shadow-xl space-y-4">
            <div className="flex items-center justify-between border-b border-[#141b2a] pb-3">
              <div>
                <h3 className="text-xs font-chakra font-black uppercase tracking-widest text-white flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  <span>METODE DE PLATĂ ACCEPTATE CU DETECȚIE AUTOMATĂ</span>
                </h3>
                <p className="text-[11px] font-mono text-zinc-400 mt-0.5">
                  Fără comisioane ascunse · Confirmare instantă prin webhook securizat
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div className="p-3.5 rounded-lg bg-[#05070d] border border-blue-500/30 flex items-center gap-3">
                <CreditCard className="w-6 h-6 text-blue-400 shrink-0" />
                <div>
                  <div className="font-chakra font-bold text-xs text-white uppercase">Card Bancar</div>
                  <div className="text-[9px] font-mono text-zinc-400">Visa / Mastercard</div>
                </div>
              </div>

              <div className="p-3.5 rounded-lg bg-[#05070d] border border-[#0079C1]/30 flex items-center gap-3">
                <Wallet className="w-6 h-6 text-[#0079C1] shrink-0" />
                <div>
                  <div className="font-chakra font-bold text-xs text-white uppercase">PayPal</div>
                  <div className="text-[9px] font-mono text-zinc-400">Instant / PayPal.Me</div>
                </div>
              </div>

              <div className="p-3.5 rounded-lg bg-[#05070d] border border-[#ee1177]/30 flex items-center gap-3">
                <Smartphone className="w-6 h-6 text-[#ee1177] shrink-0" />
                <div>
                  <div className="font-chakra font-bold text-xs text-white uppercase">Revolut</div>
                  <div className="text-[9px] font-mono text-zinc-400">Transfer @Revtag</div>
                </div>
              </div>

              <div className="p-3.5 rounded-lg bg-[#05070d] border border-amber-500/30 flex items-center gap-3">
                <Bitcoin className="w-6 h-6 text-amber-400 shrink-0" />
                <div>
                  <div className="font-chakra font-bold text-xs text-white uppercase">Crypto</div>
                  <div className="text-[9px] font-mono text-zinc-400">USDT (TRC20) / BTC</div>
                </div>
              </div>

              <div className="p-3.5 rounded-lg bg-[#05070d] border border-orange-500/30 flex items-center gap-3">
                <Gift className="w-6 h-6 text-orange-400 shrink-0" />
                <div>
                  <div className="font-chakra font-bold text-xs text-white uppercase">PaysafeCard</div>
                  <div className="text-[9px] font-mono text-zinc-400">PIN 10 / 25 / 50€</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── TAB 2: ABONAMENTE VIP ─── */}
      {tab === "vip" && (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-chakra font-black uppercase text-white tracking-wide flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-400" />
              <span>ABONAMENTE VIP & AVANTAJE EXCLUSIVE</span>
            </h2>
            <p className="text-xs font-mono text-zinc-400 mt-0.5">
              Alege nivelul de VIP dorit și activează-l instant folosind IDG Coins din portofel.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {vipTiers.map((tier) => {
              const isPopular = tier.popular;
              return (
                <div
                  key={tier.id}
                  className={`bg-[#080c14] border rounded-xl p-6 flex flex-col justify-between relative transition-all duration-300 hover:shadow-2xl ${
                    isPopular 
                      ? "border-amber-500/70 shadow-[0_0_30px_rgba(245,158,11,0.15)] bg-gradient-to-b from-amber-500/10 via-[#080c14] to-[#080c14]" 
                      : "border-[#161f30]"
                  }`}
                >
                  {/* Badge */}
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-[9px] font-chakra font-black uppercase tracking-wider"
                    style={{
                      backgroundColor: isPopular ? "#f59e0b" : "#121824",
                      color: isPopular ? "#000" : tier.color,
                      border: isPopular ? "none" : `1px solid ${tier.color}60`
                    }}
                  >
                    {tier.badge}
                  </div>

                  <div>
                    <div className="text-center pt-2 pb-4 border-b border-[#141b2a]">
                      <Crown className="w-8 h-8 mx-auto mb-2" style={{ color: tier.color }} />
                      <h3 className="font-chakra font-black text-xl text-white uppercase tracking-wide">
                        {tier.name}
                      </h3>
                      <div className="flex items-baseline justify-center gap-1.5 mt-2">
                        <span className="font-chakra text-3xl font-black" style={{ color: tier.color }}>
                          {tier.coins.toLocaleString()}
                        </span>
                        <span className="text-xs font-mono text-zinc-400 font-bold">IDG Coins</span>
                      </div>
                      <div className="text-[10px] font-mono text-zinc-400 mt-1">
                        Valabilitate: <strong className="text-white">{tier.period}</strong>
                      </div>
                    </div>

                    {/* Features Checklist */}
                    <div className="space-y-2.5 py-4">
                      {tier.features.map((feat, idx) => (
                        <div key={idx} className="flex items-start gap-2.5 text-xs text-zinc-300 font-sans">
                          <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                          <span>{feat}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Activation Button */}
                  <div className="pt-4 border-t border-[#141b2a]">
                    <button
                      onClick={() => setModal("vip")}
                      className="w-full py-3 rounded-lg font-chakra font-black text-xs uppercase tracking-wider text-black transition-all shadow-md hover:brightness-110 flex items-center justify-center gap-2"
                      style={{ backgroundColor: tier.color }}
                    >
                      <Crown className="w-4 h-4" />
                      <span>ACTIVEAZĂ {tier.name}</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── TAB 3: REVENDICĂ VOUCHER PIN ─── */}
      {tab === "voucher" && (
        <div className="bg-[#080c14] border border-[#161f30] rounded-xl p-8 max-w-2xl mx-auto shadow-2xl space-y-6">
          <div className="text-center space-y-2">
            <div className="w-16 h-16 rounded-full bg-violet-500/10 border border-violet-500/30 flex items-center justify-center text-violet-400 mx-auto shadow-[0_0_20px_rgba(139,92,246,0.2)]">
              <Gift className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-chakra font-black uppercase text-white tracking-wide">
              REVENDICĂ UN COD VOUCHER PIN
            </h2>
            <p className="text-xs text-zinc-400 font-mono max-w-md mx-auto">
              Ai primit un cod promoțional sau un voucher cadou de la concursuri? Introdu-l mai jos pentru a primi monedele instant.
            </p>
          </div>

          <div className="p-4 rounded-xl bg-[#05070d] border border-[#141b2a] space-y-4">
            <button
              onClick={() => setModal("voucher")}
              className="w-full py-3.5 rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-chakra font-black text-xs uppercase tracking-wider transition-all shadow-lg flex items-center justify-center gap-2"
            >
              <Gift className="w-4 h-4" />
              <span>DESCHIDE FORMULARUL DE REVENDICARE PIN</span>
            </button>
          </div>
        </div>
      )}

      {/* ─── TAB 4: ISTORIC TRANZACȚII ─── */}
      {tab === "history" && (
        <div className="space-y-4">
          <TransactionHistory />
        </div>
      )}

      {/* Modals */}
      {modal === "recharge" && (
        <RechargeModal 
          onClose={() => setModal(null)} 
          onSuccess={handleRechargeSuccess} 
        />
      )}
      {modal === "vip" && (
        <VipPurchaseModal 
          onClose={() => setModal(null)} 
          onSuccess={handleRechargeSuccess} 
        />
      )}
      {modal === "voucher" && (
        <VoucherModal 
          onClose={() => setModal(null)} 
          onSuccess={handleRechargeSuccess} 
        />
      )}
    </div>
  );
}
